import {
  addDoc,
  deleteDoc,
  deleteField,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol, refDoc } from "./paths";
import type {
  IndividualMoneyGoalInput,
  FamilyGoalInput,
  Goal,
} from "@/lib/schemas";

/**
 * Tworzy cel indywidualny pieniężny — `kind: "individual_money"`.
 * Dziecko może swoje, parent zawsze (egzekwowane przez rules).
 */
export async function createIndividualMoneyGoal(
  hid: string,
  createdByUid: string,
  input: IndividualMoneyGoalInput,
): Promise<string> {
  const db = getDb();
  const ref = await addDoc(refCol(db, paths.goals(hid)), {
    kind: "individual_money",
    userId: input.userId,
    title: input.title,
    ...(input.emoji ? { emoji: input.emoji } : {}),
    targetMinor: input.targetMinor,
    ...(input.fullPriceMinor !== undefined
      ? {
          fullPriceMinor: input.fullPriceMinor,
          childSharePct: input.childSharePct,
          matchedBy: input.matchedBy,
        }
      : {}),
    createdAt: serverTimestamp(),
    createdBy: createdByUid,
  });
  return ref.id;
}

/**
 * Tworzy cel rodziny — `kind: "family_count"` lub `"family_offline_count"`.
 * Tylko parent (egzekwowane przez rules).
 */
export async function createFamilyGoal(
  hid: string,
  createdByUid: string,
  input: FamilyGoalInput,
): Promise<string> {
  const db = getDb();
  const ref = await addDoc(refCol(db, paths.goals(hid)), {
    kind: input.kind,
    title: input.title,
    ...(input.emoji ? { emoji: input.emoji } : {}),
    targetCount: input.targetCount,
    createdAt: serverTimestamp(),
    createdBy: createdByUid,
  });
  return ref.id;
}

type IndividualMoneyPatch = {
  title?: string;
  emoji?: string;
  targetMinor?: number;
  /**
   * Zasada 50/50 — komplet pól albo `null` (zdjęcie zasady z celu).
   * `undefined` = nie ruszaj. Tylko parent (rules).
   */
  match?: {
    fullPriceMinor: number;
    childSharePct: number;
    matchedBy: string;
  } | null;
};

type FamilyPatch = {
  title?: string;
  emoji?: string;
  targetCount?: number;
};

/**
 * Update individual_money — tylko whitelist pól dozwolonych przez rules.
 * `kind` i `userId` niezmienne (zmiana właściciela = nowy cel).
 */
export async function updateIndividualMoneyGoal(
  hid: string,
  gid: string,
  patch: IndividualMoneyPatch,
): Promise<void> {
  const db = getDb();
  const cleaned: Record<string, unknown> = {};
  if (patch.title !== undefined) cleaned.title = patch.title;
  if (patch.emoji !== undefined) cleaned.emoji = patch.emoji;
  if (patch.targetMinor !== undefined) cleaned.targetMinor = patch.targetMinor;
  if (patch.match !== undefined) {
    if (patch.match === null) {
      cleaned.fullPriceMinor = deleteField();
      cleaned.childSharePct = deleteField();
      cleaned.matchedBy = deleteField();
    } else {
      cleaned.fullPriceMinor = patch.match.fullPriceMinor;
      cleaned.childSharePct = patch.match.childSharePct;
      cleaned.matchedBy = patch.match.matchedBy;
    }
  }
  await updateDoc(refDoc(db, paths.goal(hid, gid)), cleaned);
}

/**
 * Update family_count / family_offline_count — tylko parent.
 */
export async function updateFamilyGoal(
  hid: string,
  gid: string,
  patch: FamilyPatch,
): Promise<void> {
  const db = getDb();
  const cleaned: Record<string, unknown> = {};
  if (patch.title !== undefined) cleaned.title = patch.title;
  if (patch.emoji !== undefined) cleaned.emoji = patch.emoji;
  if (patch.targetCount !== undefined) cleaned.targetCount = patch.targetCount;
  await updateDoc(refDoc(db, paths.goal(hid, gid)), cleaned);
}

/**
 * Idempotentnie ustawia `completedAt` gdy goal osiągnął target.
 * Guard `if (snap.data()?.completedAt) return` chroni przed double-mark
 * przy race między listenerami (np. dwa device'y patrzące na ten sam HH).
 */
export async function markGoalCompletedTx(
  hid: string,
  gid: string,
): Promise<void> {
  const db = getDb();
  const ref = refDoc(db, paths.goal(hid, gid));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    if (snap.data().completedAt) return;
    tx.update(ref, { completedAt: serverTimestamp() });
  });
}

export async function deleteGoal(hid: string, gid: string): Promise<void> {
  const db = getDb();
  await deleteDoc(refDoc(db, paths.goal(hid, gid)));
}

/**
 * Type narrowing helper dla edycji — dzieli patch po kind goala.
 * Używaj zamiast wywołania update*Goal bezpośrednio gdy masz tylko `Goal`.
 */
export async function updateGoalByKind(
  hid: string,
  goal: Goal,
  patch: IndividualMoneyPatch & FamilyPatch,
): Promise<void> {
  if (goal.kind === "individual_money") {
    await updateIndividualMoneyGoal(hid, goal.id, {
      title: patch.title,
      emoji: patch.emoji,
      targetMinor: patch.targetMinor,
    });
  } else {
    await updateFamilyGoal(hid, goal.id, {
      title: patch.title,
      emoji: patch.emoji,
      targetCount: patch.targetCount,
    });
  }
}
